import NiceModal from '@ebay/nice-modal-react';

import { Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import Authorization from 'components/global/Authorization';
import Loading from 'components/global/Loading';
import PageHeader from 'components/global/PageHeader';
import Popper from 'components/global/Popper';
import Tabs from 'components/global/Tabs';

import useMediaQuery from 'hooks/useMediaQuery';
import useModalRouter from 'hooks/useModalRouter';

import ImportDataModal from 'modals/ImportDataModal';

import { importActions } from 'pages/Settings/ImportExport/ImportLog/ImportLogList';

import { Breakpoints } from 'utils/const.styles';
import PATHS, { mergePaths } from 'utils/paths';

import { faAppStoreIos, faGooglePlay } from '@fortawesome/free-brands-svg-icons';
import { faCog, faDownload, faPlus } from '@fortawesome/pro-solid-svg-icons';

import * as S from './Configuration.styled';
import UserGroupForm from './UserGroups/UserGroupForm';
import VendorForm from './Vendors/VendorForm';

const DownloadConfig = (props) => {
	const { closePopper } = props;
	const { t } = useTranslation();

	return (
		<S.PopperContainer>
			<Button
				component="a"
				icon={faAppStoreIos}
				href={t('otoPackAppStore.href')}
				target="_blank"
				label="otoPackAppStore"
				variant="borderless"
				fullWidth
				onClick={() => {
					closePopper();
				}}
			/>
			<Button
				component="a"
				icon={faGooglePlay}
				href={t('otoPackPlayStore.href')}
				target="_blank"
				label="otoPackPlayStore"
				variant="borderless"
				fullWidth
				onClick={() => {
					closePopper();
				}}
			/>
		</S.PopperContainer>
	);
};

const HeaderButtons = () => {
	const { showModal } = useModalRouter();
	const { pathname } = useLocation();
	const [buttonRef, setButtonRef] = useState(null);
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const navigate = useNavigate();

	return (
		<>
			{pathname === mergePaths(PATHS.WMS_CONFIGURATION, PATHS.BINS) && (
				<Authorization path={`/import-export/button/binImport`}>
					<Button
						className="mobile-hidden importDataButton"
						icon={faDownload}
						variant="borderless"
						label="importData"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
						onClick={() => NiceModal.show(ImportDataModal, importActions.binImport)}
					/>
				</Authorization>
			)}
			{pathname === mergePaths(PATHS.WMS_CONFIGURATION, PATHS.ZONES) && (
				<Authorization path={`/import-export/button/zoneImport`}>
					<Button
						className="mobile-hidden importDataButton"
						icon={faDownload}
						variant="borderless"
						label="importData"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
						onClick={() => NiceModal.show(ImportDataModal, importActions.zoneImport)}
					/>
				</Authorization>
			)}
			{!isMobile ? (
				<>
					<Button
						icon={faCog}
						variant="outline"
						label="editSettings"
						onClick={() => {
							navigate(PATHS.OTOPACK_SETTINGS, {
								state: { otoModule: false, showBackButton: true }
							});
						}}
					/>
					<Button
						ref={setButtonRef}
						icon={faDownload}
						size="small"
						variant="outline"
						label="downloadOtoPack"
					/>
					{buttonRef && (
						<Popper refElement={buttonRef} placement="bottom-end" offsetX={0} offsetY={1} hoverMode>
							{(closePopper) => <DownloadConfig closePopper={closePopper} />}
						</Popper>
					)}
				</>
			) : null}

			{pathname === mergePaths(PATHS.WMS_CONFIGURATION, PATHS.WMS_CONFIGURATION_USERS) && (
				<Authorization path="/modal/wms-user-form">
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						className="addButton"
						label="addUser"
						onClick={() => {
							showModal('wms-user-form');
						}}
						variant="blue"
					/>
				</Authorization>
			)}
			{pathname === mergePaths(PATHS.WMS_CONFIGURATION, PATHS.BINS) && (
				<Authorization path="/modal/bin-form">
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						className="addButton"
						label="addBin"
						onClick={() => {
							showModal('bin-form');
						}}
						variant="blue"
					/>
				</Authorization>
			)}
			{pathname === mergePaths(PATHS.WMS_CONFIGURATION, PATHS.ZONES) && (
				<Authorization path="/modal/zone-form">
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						className="addButton"
						label="addZone"
						onClick={() => {
							showModal('zone-form');
						}}
						variant="blue"
					/>
				</Authorization>
			)}
			{pathname === mergePaths(PATHS.WMS_CONFIGURATION, PATHS.VENDORS) && (
				<Authorization path="/modal/zone-form">
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						className="addButton"
						label="addVendor"
						onClick={() => {
							NiceModal.show(VendorForm, { mode: 'add' });
						}}
						variant="blue"
					/>
				</Authorization>
			)}
			{pathname === mergePaths(PATHS.WMS_CONFIGURATION, PATHS.WMS_CONFIGURATION_USER_GROUPS) && (
				<Button
					lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
					className="addButton"
					label="addUserGroup"
					onClick={() => {
						NiceModal.show(UserGroupForm, { mode: 'add' });
					}}
					variant="blue"
				/>
			)}
		</>
	);
};
const Configuration = () => {
	const tabs = useMemo(
		() => [
			{
				label: 'users',
				path: mergePaths(PATHS.WMS_CONFIGURATION, PATHS.WMS_CONFIGURATION_USERS)
			},
			{
				label: 'userGroups',
				path: mergePaths(PATHS.WMS_CONFIGURATION, PATHS.WMS_CONFIGURATION_USER_GROUPS)
			},
			{
				label: 'bins',
				path: mergePaths(PATHS.WMS_CONFIGURATION, PATHS.BINS)
			},
			{
				label: 'zones',
				path: mergePaths(PATHS.WMS_CONFIGURATION, PATHS.ZONES)
			},
			{
				label: 'vendors',
				path: mergePaths(PATHS.WMS_CONFIGURATION, PATHS.VENDORS)
			}
		],
		[]
	);

	return (
		<div>
			<PageHeader
				title="wms.configuration"
				moduleName="warehouse-management-system"
				lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/configuration-icon.json.gz">
				<HeaderButtons />
			</PageHeader>
			<Tabs data={tabs} checkURL />
			<Suspense fallback={<Loading />}>
				<Outlet />
			</Suspense>
		</div>
	);
};

export default Configuration;
